import { ENTITIES_COLUMNS } from 'constants/analytics';
import { IColumnMetadata } from 'contexts/analytics/types';
import { SortState } from 'types/sorting';
import { toOrdering } from 'utils/sorting';
import { getExportType } from 'utils/tableCsvExport';
import { getPageExportTableSet } from 'utils/pageExportTables';
import { parseRetryAfter } from './http';
import {
  PageExportPreflightPayload,
  PageExportPreflightResponse,
  getPageExportPreflight,
} from 'services/analyticsExports';

export type PreflightFailureReason =
  | 'permission_denied'
  | 'request_failed'
  | 'throttled'
  | 'upstream_analytics_failed'
  | 'unexpected_response_shape'
  | 'timeout'
  | 'no_data';

export type PreflightResult =
  | { ok: true; data: PageExportPreflightResponse }
  | { ok: false; reason: PreflightFailureReason; retryAfter?: number };

export interface BuildPreflightPayloadArgs {
  format: 'pdf' | 'zip';
  accountId: string;
  teamIds: string[];
  dateFrom?: string | null;
  dateTo?: string | null;
  period?: string | null;
  currentChart: string;
  currentEntity: string;
  tableOrdering?: SortState | null;
}

export function buildActiveOrdering(
  currentChart: string,
  currentEntity: string,
  tableOrdering?: SortState | null
): { export_type: string; ordering: string } | undefined {
  const exportType = getExportType(currentChart, currentEntity);
  if (!exportType || !tableOrdering) return undefined;

  const columns: IColumnMetadata[] =
    ENTITIES_COLUMNS[currentChart?.toUpperCase()]?.[currentEntity] ?? [];
  const columnKeyMap = columns.reduce<Record<string, string>>((acc, col) => {
    if (col.sortable && col.sortKey) acc[col.name] = col.sortKey;
    return acc;
  }, {});

  const ordering = toOrdering(tableOrdering, columnKeyMap);
  if (!ordering) return undefined;

  return { export_type: exportType, ordering };
}

export function buildPreflightPayload(args: BuildPreflightPayloadArgs): PageExportPreflightPayload {
  const payload: PageExportPreflightPayload = {
    format: args.format,
    account_id: args.accountId,
    export_types: getPageExportTableSet().map((table) => table.exportType),
    team_ids: args.teamIds,
    date_from: args.dateFrom,
    date_to: args.dateTo,
    period: args.period,
    filters: { filter_licensed_users: true },
  };

  const activeOrdering = buildActiveOrdering(
    args.currentChart,
    args.currentEntity,
    args.tableOrdering
  );
  if (activeOrdering) payload.active_ordering = activeOrdering;

  return payload;
}

const FAILURE_REASONS: readonly PreflightFailureReason[] = [
  'permission_denied',
  'request_failed',
  'throttled',
  'upstream_analytics_failed',
  'unexpected_response_shape',
  'timeout',
  'no_data',
];

function parseReason(error: unknown): PreflightFailureReason {
  const reason = (error as { response?: { data?: { reason?: string } } })?.response?.data?.reason;
  return FAILURE_REASONS.includes(reason as PreflightFailureReason)
    ? (reason as PreflightFailureReason)
    : 'request_failed';
}

export async function runPageExportPreflight(
  payload: PageExportPreflightPayload
): Promise<PreflightResult> {
  try {
    const response = await getPageExportPreflight(payload);
    return { ok: true, data: response.data };
  } catch (error) {
    const reason = parseReason(error);
    const headers = (error as { response?: { headers?: unknown } })?.response?.headers;
    const retryAfter = reason === 'throttled' ? parseRetryAfter(headers) : undefined;
    return { ok: false, reason, retryAfter };
  }
}
